import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, Mail, Globe, MapPin,
  GraduationCap, Calendar, FileText, CheckCircle2,
  Clock, AlertCircle, Save, Eye, Check, X, Home, Car
} from 'lucide-react'
import axiosInstance from '../../services/axiosInstance'
import ChatPanel from '../../components/common/ChatPanel'
import RejectDocumentModal from '../../components/modals/RejectDocumentModal'
import { useAuth } from '../../contexts/AuthContext'
import { isAgent } from '../../utils/roleUtils'
import './styles.scss'

export default function ApplicationDetails() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const agentView = isAgent(user)

  const [details, setDetails] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [status, setStatus] = useState('')
  const [savingStatus, setSavingStatus] = useState(false)
  const [checklists, setChecklists] = useState([])
  const [viewingDoc, setViewingDoc] = useState(null)
  const [updatingDoc, setUpdatingDoc] = useState(null)
  const [prefilledMessage, setPrefilledMessage] = useState('')
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [rejectItem, setRejectItem] = useState(null)
  const [rejectReason, setRejectReason] = useState('')
  const [chatRefreshTrigger, setChatRefreshTrigger] = useState(0)

  useEffect(() => {
    fetchDetails()
  }, [id])

  const fetchDetails = async () => {
    try {
      setLoading(true)
      const response = await axiosInstance.get(`/api/applications/admin/${id}`)
      if (response.data.success) {
        setDetails(response.data.data)
        setStatus(response.data.data.application.status)

        // Fetch checklists
        try {
          const chkRes = await axiosInstance.get(`/api/checklists/application/${id}`)
          setChecklists(chkRes.data || [])
        } catch (e) {
          console.error("Failed to fetch checklists", e)
        }

      } else {
        setError(response.data.message || 'Failed to fetch details')
      }
    } catch (err) {
      setError(err.message || 'Error connecting to server')
    } finally {
      setLoading(false)
    }
  }

  const handleStatusSave = async () => {
    try {
      setSavingStatus(true)
      const response = await axiosInstance.put(`/api/applications/admin/${id}/status`, { status })
      if (response.data.success) {
        setDetails(response.data.data)
        // Optionally show a toast notification here
      } else {
        alert(response.data.message || 'Failed to update status')
      }
    } catch (err) {
      alert(err.message || 'Error connecting to server')
    } finally {
      setSavingStatus(false)
    }
  }

  const handleViewDocument = async (documentId) => {
    try {
      setViewingDoc(documentId)
      const response = await axiosInstance.get(`/api/documents/${documentId}/download`, {
        responseType: 'blob' // Important to handle the binary data correctly
      })

      // Create a blob URL and open it in a new tab for native viewing
      const file = new Blob([response.data], { type: response.headers['content-type'] })
      const fileURL = URL.createObjectURL(file)
      window.open(fileURL, '_blank')

    } catch (err) {
      alert("Failed to securely load the document.")
    } finally {
      setViewingDoc(null)
    }
  }

  const handleUpdateChecklistStatus = async (item, newStatus, reason = '') => {
    try {
      setUpdatingDoc(item.id)
      const payload = {
        status: newStatus,
        documentId: item.relatedDocumentId
      }
      if (reason) {
        payload.reason = reason
      }

      const response = await axiosInstance.put(`/api/checklists/${item.id}/status`, payload)

      if (response.status === 200) {
        // Update local state to reflect change instantly
        setChecklists(prev => prev.map(c => c.id === item.id ? { ...c, status: newStatus } : c))
        // Refresh the chat panel to fetch new backend-generated messages
        setChatRefreshTrigger(prev => prev + 1)
      }
    } catch (err) {
      alert("Failed to update document status.")
    } finally {
      setUpdatingDoc(null)
    }
  }

  const handleRejectClick = (item) => {
    setRejectItem(item)
    setRejectReason('')
    setShowRejectModal(true)
  }

  const confirmReject = () => {
    if (!rejectReason.trim()) {
      alert('Please provide a reason for rejection.')
      return
    }
    setShowRejectModal(false)
    handleUpdateChecklistStatus(rejectItem, 'Action Required', rejectReason)
  }

  const handleUpdateI20Status = async (field, value) => {
    try {
      setSavingStatus(true)
      const response = await axiosInstance.put(`/api/i20-visa/admin/${id}`, { [field]: value })
      if (response.data.success) {
        setDetails(prev => ({ ...prev, i20Profile: response.data.data.i20Profile }))
      } else {
        alert(response.data.message || 'Failed to update request status')
      }
    } catch (err) {
      alert(err.message || 'Error connecting to server')
    } finally {
      setSavingStatus(false)
    }
  }

  const getStatusBadge = (currentStatus) => {
    switch (currentStatus) {
      case 'SUBMITTED':
      case 'APPLICATION_STARTED':
        return <span className="badge badge--warning"><Clock size={14} /> {currentStatus.replace('_', ' ')}</span>
      case 'UNDER_REVIEW':
        return <span className="badge badge--primary"><FileText size={14} /> In Review</span>
      case 'ADMITTED':
      case 'APPROVED':
        return <span className="badge badge--success"><CheckCircle2 size={14} /> Admitted</span>
      case 'REJECTED':
        return <span className="badge badge--danger"><AlertCircle size={14} /> Rejected</span>
      default:
        return <span className="badge badge--neutral">{currentStatus}</span>
    }
  }

  if (loading) return <div className="app-details-loading">Loading application details...</div>
  if (error) return <div className="app-details-error">{error}</div>
  if (!details) return <div className="app-details-error">Application not found</div>

  const app = details.application

  return (
    <div className="application-details">
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <button className="btn-back" onClick={() => navigate('/applications')}>
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="title-row">
              <h1>{details.studentName || 'Unknown Student'}</h1>
              {getStatusBadge(app.status)}
            </div>
            <p className="subtitle">Application #{app.applicationNumber} &bull; Submitted {new Date(app.createdAt).toLocaleDateString()}</p>
          </div>
        </div>
      </div>

      <div className="details-grid">
        {/* Left Column: Profile & Academic */}
        <div className="details-main">

          <div className="glass-panel detail-card">
            <h3>Applicant Profile</h3>
            <div className="info-list">
              <div className="info-item">
                <Mail className="info-icon" size={18} />
                <div>
                  <label>Email Address</label>
                  <span>{details.studentEmail || '-'}</span>
                </div>
              </div>
              <div className="info-item">
                <Globe className="info-icon" size={18} />
                <div>
                  <label>Citizenship</label>
                  <span>{details.studentCitizenship || '-'}</span>
                </div>
              </div>
              <div className="info-item">
                <MapPin className="info-icon" size={18} />
                <div>
                  <label>Current Residence</label>
                  <span>{details.studentResidence || '-'}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="glass-panel detail-card">
            <h3>Academic Selection</h3>
            <div className="info-list">
              <div className="info-item">
                <GraduationCap className="info-icon" size={18} />
                <div>
                  <label>Program</label>
                  <span>{details.programName || app.programId}</span>
                </div>
              </div>
              <div className="info-item">
                <Calendar className="info-icon" size={18} />
                <div>
                  <label>Intake Term</label>
                  <span>{details.intakeName || app.intakeId}</span>
                </div>
              </div>
              <div className="info-item">
                <FileText className="info-icon" size={18} />
                <div>
                  <label>Degree Level</label>
                  <span>{details.degreeLevel || 'Master'}</span>
                </div>
              </div>
            </div>
          </div>

          {!agentView && details.i20Profile && (details.i20Profile.accommodationStatus === 'NEEDS_HELP' || details.i20Profile.airportPickupRequested === 'REQUESTED') && (
            <div className="glass-panel detail-card" style={{ borderColor: '#fcd34d', backgroundColor: '#fffbeb' }}>
              <h3 style={{ color: '#b45309' }}>Pre-Departure Requests</h3>
              <p className="section-desc">The student has requested assistance with the following items.</p>
              <div className="info-list" style={{ flexDirection: 'column' }}>
                {details.i20Profile.accommodationStatus === 'NEEDS_HELP' && (
                  <div className="info-item" style={{ width: '100%', justifyContent: 'space-between', padding: '1rem', background: 'white', borderRadius: '8px', border: '1px solid #fde68a' }}>
                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                      <Home className="info-icon" size={18} color="#d97706" />
                      <div>
                        <label>Housing Assistance</label>
                        <span style={{ color: '#d97706', fontWeight: 500 }}>Requested</span>
                      </div>
                    </div>
                    <button 
                      className="btn btn-sm btn-primary" 
                      onClick={() => handleUpdateI20Status('accommodationStatus', 'SECURED')}
                      disabled={savingStatus}
                    >
                      Mark Resolved (Housing Secured)
                    </button>
                  </div>
                )}
                {details.i20Profile.airportPickupRequested === 'REQUESTED' && (
                  <div className="info-item" style={{ width: '100%', justifyContent: 'space-between', padding: '1rem', background: 'white', borderRadius: '8px', border: '1px solid #fde68a', marginTop: '1rem' }}>
                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                      <Car className="info-icon" size={18} color="#d97706" />
                      <div>
                        <label>Airport Pickup</label>
                        <span style={{ color: '#d97706', fontWeight: 500 }}>Requested</span>
                      </div>
                    </div>
                    <button 
                      className="btn btn-sm btn-primary" 
                      onClick={() => handleUpdateI20Status('airportPickupRequested', 'ARRANGED')}
                      disabled={savingStatus}
                    >
                      Mark Resolved (Pickup Arranged)
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="glass-panel detail-card document-checklist-card">
            <h3>Document Checklist</h3>
            <p className="section-desc">Uploaded documents required for this application.</p>

            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Requirement</th>
                    <th>Status</th>
                    <th className="action-col">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {checklists.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="empty-row">No checklist items generated yet.</td>
                    </tr>
                  ) : (
                    checklists.map((item, index) => (
                      <tr key={item.id}>
                        <td>{index + 1}. {item.title}</td>
                        <td>
                          {item.status === 'Completed' || item.status === 'Under Review' ? (
                            <span className="badge badge--success"><CheckCircle2 size={12} /> {item.status}</span>
                          ) : (
                            <span className="badge badge--neutral">{item.status}</span>
                          )}
                        </td>
                        <td className="action-col">
                          {item.relatedDocumentId ? (
                            <div className="action-buttons" style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                              <button
                                className="btn btn-outline btn-sm"
                                onClick={() => handleViewDocument(item.relatedDocumentId)}
                                disabled={viewingDoc === item.relatedDocumentId || updatingDoc === item.id}
                              >
                                {viewingDoc === item.relatedDocumentId ? 'Loading...' : <><Eye size={14} /> View</>}
                              </button>

                              {item.status === 'Under Review' && !agentView && (
                                <>
                                  <button
                                    className="btn btn-sm"
                                    style={{ backgroundColor: '#10b981', color: 'white', border: 'none', display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.35rem 0.75rem', borderRadius: '6px', fontWeight: '500', cursor: 'pointer' }}
                                    onClick={() => handleUpdateChecklistStatus(item, 'Completed')}
                                    disabled={updatingDoc === item.id}
                                  >
                                    <Check size={14} /> Approve
                                  </button>
                                  <button
                                    className="btn btn-sm"
                                    style={{ backgroundColor: '#ef4444', color: 'white', border: 'none', display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.35rem 0.75rem', borderRadius: '6px', fontWeight: '500', cursor: 'pointer' }}
                                    onClick={() => handleRejectClick(item)}
                                    disabled={updatingDoc === item.id}
                                  >
                                    <X size={14} /> Reject
                                  </button>
                                </>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted text-sm">Not Uploaded</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </div>

        {/* Right Column: Actions */}
        <div className="details-sidebar">

          {!agentView && (
            <div className="glass-panel action-card">
              <h3>Update Status</h3>
              <p className="action-desc">Change the current state of this application.</p>

              <div className="status-select-wrapper">
                <select
                  className="status-select"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="APPLICATION_STARTED">Application Started</option>
                  <option value="SUBMITTED">Submitted</option>
                  <option value="UNDER_REVIEW">Under Review</option>
                  <option value="ADMITTED">Admitted</option>
                  <option value="REJECTED">Rejected</option>
                </select>
              </div>

              <button
                className="btn btn-primary w-full"
                onClick={handleStatusSave}
                disabled={savingStatus || status === app.status}
              >
                {savingStatus ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}

          <div style={{ marginTop: agentView ? '0' : '1.5rem' }}>
            <ChatPanel
              studentProfileId={details.application.studentProfileId}
              initialText={prefilledMessage}
              onClearInitialText={() => setPrefilledMessage('')}
              refreshTrigger={chatRefreshTrigger}
            />
          </div>

        </div>
      </div>

      {/* Reject Modal */}
      <RejectDocumentModal
        show={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        onConfirm={confirmReject}
        rejectItem={rejectItem}
        rejectReason={rejectReason}
        setRejectReason={setRejectReason}
      />
    </div>
  )
}
